import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { ChevronRight, Search } from "lucide-react";
import { farms } from "@/data/farms";
import { FarmCard } from "@/components/farm/FarmCard";

export default function FarmStoriesPage() {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredFarms = farms.filter((farm) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      farm.name.toLowerCase().includes(query) ||
      farm.location.toLowerCase().includes(query) ||
      farm.farmerName.toLowerCase().includes(query)
    );
  });

  return (
    <div>
      {/* Hero */}
      <section className="relative py-16 lg:py-24">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1588152850700-c82ecb8ba9b1?w=1920&q=80"
            alt="Dutch sheep farm"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground">Farm Stories</span>
          </nav>

          <div className="max-w-2xl">
            <h1 className="font-display text-4xl sm:text-5xl font-semibold text-foreground">
              Farm Stories
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Meet the farmers who make WoolStory possible. Each farm has its own history, 
              its own flock, and its own approach to sustainable sheep farming.
            </p>

            {/* Search */}
            <div className="mt-8 relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search farms by name or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Farms Grid */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredFarms.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {filteredFarms.map((farm) => (
                <FarmCard key={farm.id} farm={farm} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground">No farms match your search.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-secondary/30 border-t border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-foreground">
            Want to visit a farm?
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Some of our partner farms welcome visitors during certain seasons. 
            Get in touch to learn about farm visit opportunities.
          </p>
          <Link
            to="#"
            className="inline-flex items-center mt-6 text-foreground font-medium hover:underline"
          >
            Contact us about farm visits →
          </Link>
        </div>
      </section>
    </div>
  );
}
